import type { ResolveStuckStepResult } from '@/modules/wizard/functions/resolve-stuck-step-result.type'

/**
 * Decides how to reconcile a saved wizard step index that may no longer be
 * renderable (e.g. its `if`/`unless` conditions changed after the flow was last
 * visited).
 *
 * We only ever move **forward** or complete — never backward. A saved index
 * means the user already passed every earlier step, so bouncing back would
 * re-show an already-answered question. This is exactly what happened with the
 * EEB placement experiment (OCPT-860): a user who saw the EEB booking step in
 * onboarding (control / not-yet-settled flag) and later resolved to variant B —
 * where `consultation_booking` is removed — would otherwise be walked back to
 * the preceding `hzb_select` (Schulabschluss) step.
 *
 * @param stepCount Total number of steps in the flow.
 * @param savedIndex The persisted step index to reconcile.
 * @param isRenderable Predicate returning whether the step at an index renders.
 */
export const resolveStuckStep = (
  stepCount: number,
  savedIndex: number,
  isRenderable: (index: number) => boolean,
): ResolveStuckStepResult => {
  if (stepCount <= 0) return { type: 'stay' }
  if (isRenderable(savedIndex)) return { type: 'stay' }

  for (let i = savedIndex + 1; i < stepCount; i++) {
    if (isRenderable(i)) return { type: 'goto', index: i }
  }

  for (let i = 0; i < stepCount; i++) {
    if (isRenderable(i)) return { type: 'complete' }
  }

  return { type: 'error' }
}
